 import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, map } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../environments/environment';

/**
 * Resultado de geocodificación inversa.
 */
export interface GeocodingResult {
  address: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
}

@Injectable({ providedIn: 'root' })
export class GeocodingService {
  private readonly apiKey: string = environment.googleMaps?.apiKey || '';

  constructor(
    private http: HttpClient,
    private translate: TranslateService
  ) {}

  /**
   * Obtiene la dirección a partir de coordenadas usando Google Maps Geocoding API.
   * Si la llamada principal falla, intenta con los fallbacks de geocodificación.
   *
   * @param location Coordenadas { lat, lng }
   * @param geocoder Instancia opcional de google.maps.Geocoder (web) para fallback SDK
   * @returns Promesa con el resultado de geocodificación o null si no se pudo resolver
   */
  async getAddressFromCoordinates(
    location: { lat: number; lng: number },
    geocoder?: any
  ): Promise<GeocodingResult | null> {
    if (!location) {
      return null;
    }

    return new Promise<GeocodingResult | null>((resolve) => {
      this.getAddressByLatLong(location.lat, location.lng).subscribe({
        next: (data) => {
          if (data) {
            const result: GeocodingResult = {
              address: data.display_name,
              city:
                data.address?.city ||
                data.address?.town ||
                data.address?.village,
              province: data.address?.state,
              postalCode: data.address?.postcode,
              country: data.address?.country,
            };
            resolve(result);
          } else {
            // REST API returned empty results — fall back to Google Maps SDK/API
            this.fallbackToGoogleGeocoding(location, geocoder)
              .then((result) => resolve(result))
              .catch(() => resolve(null));
          }
        },
        error: () => {
          // Si falla la llamada principal, intentar con los fallbacks
          this.fallbackToGoogleGeocoding(location, geocoder)
            .then((result) => resolve(result))
            .catch(() => resolve(null));
        },
      });
    });
  }

  /**
   * Llama a la API REST de Google Maps Geocoding para obtener la dirección.
   *
   * @param latitude Latitud
   * @param longitude Longitud
   * @returns Observable con los datos de geocodificación en formato normalizado
   */
  getAddressByLatLong(latitude: number, longitude: number): Observable<any> {
    const geocodeUrl = 'https://maps.googleapis.com/maps/api/geocode/json';
    const language = 'es';

    const url = `${geocodeUrl}?latlng=${latitude},${longitude}&language=${language}&key=${this.apiKey}`;

    return this.http.get(url).pipe(
      map((data: any) => {
        if (data && data.results && data.results.length > 0) {
          return {
            display_name: data.results[0].formatted_address,
            address: this.extractAddressComponents(data.results[0]),
          };
        }
        return null;
      }),
      catchError(this.handleGeocodingError<any>(this.translate.instant('MAP.GET_ADDRESS_GOOGLE'), null))
    );
  }

  /**
   * Extrae los componentes de dirección de una respuesta de Google Geocoding.
   *
   * @param result Resultado individual de Google Geocoding API
   * @returns Objeto con city, state, postcode, country, town
   */
  extractAddressComponents(result: any): any {
    if (!result || !result.address_components) {
      return {};
    }

    const addressComponents = result.address_components;
    const addressDetails: any = {};

    addressComponents.forEach((component: any) => {
      const types = component.types;

      if (types.includes('locality')) {
        addressDetails.city = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        addressDetails.state = component.long_name;
      } else if (types.includes('postal_code')) {
        addressDetails.postcode = component.long_name;
      } else if (types.includes('country')) {
        addressDetails.country = component.long_name;
      } else if (
        types.includes('administrative_area_level_2') &&
        !addressDetails.city
      ) {
        addressDetails.town = component.long_name;
      }
    });

    return addressDetails;
  }

  /**
   * Manejador genérico de errores HTTP para operaciones de geocodificación.
   *
   * @param operation Nombre de la operación que falló (para logging)
   * @param result Valor por defecto a devolver en caso de error
   * @returns Función que retorna un Observable con el valor por defecto
   */
  handleGeocodingError<T>(operation?: string, result?: T) {
    return (error: any): Observable<T> => {
      const op = operation || this.translate.instant('COMMON.OPERATION');
      return of(result as T);
    };
  }

  /**
   * Fallback de geocodificación usando el SDK de Google Maps (google.maps.Geocoder).
   * Si no hay geocoder disponible, intenta con la API REST como último recurso.
   *
   * @param location Coordenadas { lat, lng }
   * @param geocoder Instancia opcional de google.maps.Geocoder
   * @returns Promesa con el resultado de geocodificación o null
   */
  async fallbackToGoogleGeocoding(
    location: { lat: number; lng: number },
    geocoder?: any
  ): Promise<GeocodingResult | null> {
    if (typeof google !== 'undefined' && geocoder) {
      return new Promise<GeocodingResult | null>((resolve) => {
        geocoder.geocode({ location }, (results: any, status: any) => {
          if (status === 'OK' && results[0]) {
            const components = this.extractAddressComponents(results[0]);
            resolve({
              address: results[0].formatted_address,
              city: components.city || components.town,
              province: components.state,
              postalCode: components.postcode,
              country: components.country,
            });
          } else {
            resolve(null);
          }
        });
      });
    } else if (
      Capacitor.getPlatform() === 'android' ||
      Capacitor.getPlatform() === 'ios'
    ) {
      return this.fallbackToGoogleGeocodingAPI(location);
    }

    return null;
  }

  /**
   * Último recurso: usa la API REST de Google Maps Geocoding directamente vía fetch.
   *
   * @param location Coordenadas { lat, lng }
   * @returns Promesa con el resultado de geocodificación o null
   */
  async fallbackToGoogleGeocodingAPI(location: {
    lat: number;
    lng: number;
  }): Promise<GeocodingResult | null> {
    if (!environment.googleMaps?.apiKey) {
      return null;
    }

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${location.lat},${location.lng}&key=${environment.googleMaps.apiKey}`
      );
      const data = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const components = this.extractAddressComponents(data.results[0]);
        return {
          address: data.results[0].formatted_address,
          city: components.city || components.town,
          province: components.state,
          postalCode: components.postcode,
          country: components.country,
        };
      }

      return null;
    } catch (error) {
      return null;
    }
  }
}
